// Auth cache — survival strategy for "warm device, offline at launch".
//
// On a successful GET /me, stash the response in localStorage so a
// later cold start without network can still render the app. Without
// this, AuthProvider's /me fetch rejects with a network TypeError and
// the user is stuck on an error state — even though the session
// cookie is still valid and PowerSync's local SQLite has all the
// data the user needs.
//
// Trust model:
//   - The cached payload is treated as evidence the user WAS
//     authenticated up to MAX_AGE ago. It does NOT bypass the live
//     /me check when one succeeds — every successful /me overwrites
//     the cache and the live response is the source of truth.
//   - A 401 from /me clears the cache, so a cookie that the server
//     has invalidated never lets a stale cache "log the user back in"
//     after they reconnect.
//   - The cache is bounded by MAX_AGE so even a permanently-offline
//     device can't keep working indefinitely on a long-rotted login.
//
// Why localStorage and not IndexedDB:
//   - Synchronous reads — we need the cached value during the very
//     first render of AuthProvider, before any async effect has run.
//     IndexedDB's only API is async; using it here would force every
//     consumer of useAuth() to handle an extra "checking cache" state.
//   - Tiny payload (one MeResponse, < 500 bytes); no need for the
//     async-friendly machinery IndexedDB exists for.
//   - Origin-scoped (same as IndexedDB), HttpOnly cookies are
//     elsewhere — no new attack surface.
//
// Versioned key: bumping AUTH_CACHE_KEY in a future migration
// invalidates all old caches at once without per-engineer cleanup.
//
// Session-bound expiry (Codex P1 on PR #384):
//   The cache also respects the `sessionExpiresAt` value the backend
//   publishes on /me (sourced from the session JWT's `exp` claim).
//   loadCachedMe rejects an entry whose sessionExpiresAt is in the
//   past, so an offline fallback cannot outlive the real server-side
//   session. AUTH_CACHE_MAX_AGE_MS is retained as a defence-in-depth
//   ceiling for cache entries that lack the field (legacy fixtures
//   from before the field was added) and as a hard cap if the
//   backend ever publishes an unrealistically far-future expiry.

import type { MeResponse, UserRole } from '../hooks/use-me'

import { ApiError } from '../hooks/api'

/** Versioned cache key. Bump on schema-breaking changes. */
export const AUTH_CACHE_KEY = 'citycare:auth-cache:v1'

/**
 * Hard upper bound on cache age, applied even when the entry has a
 * sessionExpiresAt. Set to the session cookie lifetime (24 h, see
 * apps/backend/src/auth/session.ts :: SESSION_MAX_AGE_SECONDS) so a
 * cache entry written near the cookie's issue time can survive at
 * most one cookie lifetime offline — matching the server-enforced
 * session ceiling. The PRIMARY check is sessionExpiresAt (when
 * present); this value is the fallback for legacy cache entries
 * and a backstop against runaway server-side expiry values.
 *
 * Keep this in lockstep with SESSION_MAX_AGE_SECONDS. If the
 * backend ever bumps the cookie lifetime, this constant must move
 * with it — otherwise the cache becomes the limiting factor and
 * starts evicting still-valid sessions early.
 */
export const AUTH_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000

interface CachedMeEnvelope {
  /** Schema version of the envelope itself, in case we evolve fields. */
  v: 1
  /** Epoch ms when the payload was written. */
  cachedAt: number
  me: MeResponse
}

/** Type-narrow guard for a parsed envelope. Untrusted input — never assume. */
function isCachedMeEnvelope(value: unknown): value is CachedMeEnvelope {
  if (typeof value !== 'object' || value === null) return false
  const e = value as Record<string, unknown>
  if (e['v'] !== 1) return false
  if (typeof e['cachedAt'] !== 'number' || !Number.isFinite(e['cachedAt'])) {
    return false
  }
  const me = e['me']
  if (typeof me !== 'object' || me === null) return false
  const m = me as Record<string, unknown>
  if (typeof m['userID'] !== 'string') return false
  if (typeof m['entraOid'] !== 'string') return false
  if (typeof m['displayName'] !== 'string') return false
  const role: unknown = m['role']
  if (role !== 'admin' && role !== 'field_staff') return false
  if (
    m['environmentName'] !== undefined &&
    typeof m['environmentName'] !== 'string'
  ) {
    return false
  }
  // sessionExpiresAt is optional in the type but, when present, MUST
  // be a finite number (Unix ms). A non-numeric/garbage value would
  // make the expiry comparison meaningless, so reject the envelope
  // entirely rather than silently treating it as "no boundary known".
  const sessionExpiresAt: unknown = m['sessionExpiresAt']
  if (
    sessionExpiresAt !== undefined &&
    (typeof sessionExpiresAt !== 'number' ||
      !Number.isFinite(sessionExpiresAt))
  ) {
    return false
  }
  return true
}

/**
 * Minimum storage surface the helpers need. Exported so test
 * fixtures can declare an in-memory stand-in that's structurally
 * compatible without depending on the lib.dom Storage interface
 * (which adds `length` + an indexer signature we don't use).
 */
export interface CacheStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

/**
 * Resolve a usable storage backend. localStorage may be missing or
 * throw on access in some sandboxes (Safari private mode used to,
 * SSR in tests, etc.). Returning null lets callers no-op safely.
 *
 * Test seam: pass an explicit storage to override discovery.
 */
function getStorage(storage?: CacheStorage): CacheStorage | null {
  if (storage) return storage
  try {
    if (typeof globalThis === 'undefined') return null
    const candidate = (globalThis as { localStorage?: CacheStorage })
      .localStorage
    if (!candidate) return null
    // Probe — some Safari modes throw on access, not on read of property.
    candidate.getItem(AUTH_CACHE_KEY)
    return candidate
  } catch {
    return null
  }
}

/**
 * Persist the latest /me response. Called after every successful
 * online bootstrap so the cache reflects the freshest server state.
 *
 * No-op (silent) if storage is unavailable — the in-memory state
 * machine is unaffected; only the offline-survival path degrades.
 */
export function saveCachedMe(
  me: MeResponse,
  options: { storage?: CacheStorage; now?: () => number } = {},
): void {
  const store = getStorage(options.storage)
  if (!store) return
  const now = options.now ?? Date.now
  const envelope: CachedMeEnvelope = { v: 1, cachedAt: now(), me }
  try {
    store.setItem(AUTH_CACHE_KEY, JSON.stringify(envelope))
  } catch {
    // Quota errors / disk full / etc. — non-fatal.
  }
}

/**
 * Read the cached /me, if any. Returns null when:
 *   - storage is unavailable
 *   - the entry is missing
 *   - the JSON is malformed or the shape doesn't match
 *   - the entry is older than AUTH_CACHE_MAX_AGE_MS
 *
 * The synchronous shape (no Promise) is deliberate — AuthProvider
 * needs to consult this during its initial render path.
 */
export function loadCachedMe(
  options: { storage?: CacheStorage; now?: () => number } = {},
): MeResponse | null {
  const store = getStorage(options.storage)
  if (!store) return null
  const now = options.now ?? Date.now
  let raw: string | null
  try {
    raw = store.getItem(AUTH_CACHE_KEY)
  } catch {
    return null
  }
  if (raw === null) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    // Malformed — clear so subsequent reads don't trip on the same
    // garbage. This is best-effort; ignore secondary failures.
    try {
      store.removeItem(AUTH_CACHE_KEY)
    } catch {
      // intentional: cleanup is best-effort
    }
    return null
  }
  if (!isCachedMeEnvelope(parsed)) return null
  const nowMs = now()
  // Defence-in-depth ceiling: even if the backend publishes a
  // far-future sessionExpiresAt, no cache entry survives more than
  // one session-cookie lifetime past its write timestamp.
  if (nowMs - parsed.cachedAt > AUTH_CACHE_MAX_AGE_MS) return null
  // PRIMARY check: if the backend told us when this session expires,
  // treat the cache as invalid the moment the session would have.
  // This is the Codex P1 fix — without it, cached auth could outlive
  // the real cookie by up to AUTH_CACHE_MAX_AGE_MS in pathological
  // cases (cache written near cookie issue time on a fresh login).
  const sessionExpiresAt = parsed.me.sessionExpiresAt
  if (typeof sessionExpiresAt === 'number' && nowMs >= sessionExpiresAt) {
    return null
  }
  return parsed.me
}

/**
 * Drop the cache. Call on 401 (server-rejected session) and on
 * explicit logout. After clear, loadCachedMe returns null until the
 * next saveCachedMe.
 */
export function clearCachedMe(
  options: { storage?: CacheStorage } = {},
): void {
  const store = getStorage(options.storage)
  if (!store) return
  try {
    store.removeItem(AUTH_CACHE_KEY)
  } catch {
    // Non-fatal.
  }
}

/**
 * Is `err` the "we couldn't reach the network" shape, vs. an HTTP
 * error from a server we DID reach?
 *
 * We split on these because the response is different:
 *   - Network error ⇒ try the cache; user might be offline with a
 *                     valid session.
 *   - HTTP error    ⇒ surface to the user; the server told us
 *                     something specific, don't paper over it.
 *
 * Detection: `fetch()` rejects with a TypeError when DNS, TCP, TLS,
 * or the network layer fails. Also treat AbortError as "not a real
 * server response, don't override." `navigator.onLine === false` is
 * a strong hint but not authoritative (some networks report online
 * while still being unreachable — captive portals, VPN handoffs),
 * so we use it as a tiebreaker, not a primary signal.
 */
export function isOfflineNetworkError(err: unknown): boolean {
  if (err instanceof ApiError) return false
  // TypeError from fetch is the standard offline shape across browsers.
  if (err instanceof TypeError) return true
  // DOMException with name 'NetworkError' (Firefox) or AbortError that
  // wraps a connection-level abort.
  if (err instanceof Error && err.name === 'NetworkError') return true
  // Last-resort hint: navigator.onLine. Don't trust this alone but use
  // it to upgrade an unknown error to "probably offline" when the
  // browser already thinks the network is down.
  if (
    typeof navigator !== 'undefined' &&
    navigator.onLine === false &&
    err instanceof Error
  ) {
    return true
  }
  return false
}

/* v8 ignore start */
// Re-export the type to keep the call sites' import line short. Pure
// re-export, not exercisable in unit tests directly.
export type { MeResponse, UserRole }
/* v8 ignore stop */
